import { useCallback, useState } from 'react';
import { useUserType } from '@org/ui';

/**
 * Which scheduled classes the student wants to see: only her regular course
 * sessions (group / private / tutoring) or everything, including the extra
 * community programs (Conversations2Empower, Grammar2Empower, ...).
 *
 * The choice is remembered per device. Clube E2E students (user type
 * `community`) only ever have extra classes, so they always see everything
 * and never get the toggle.
 */
export type ClassKind = 'regular' | 'all';

export const CLASS_KIND_STORAGE_KEY = 'p2e-class-kind';
const DEFAULT_CLASS_KIND: ClassKind = 'regular';

const isClassKind = (value: unknown): value is ClassKind => value === 'regular' || value === 'all';

export function readStoredClassKind(): ClassKind {
  try {
    const stored = window.localStorage.getItem(CLASS_KIND_STORAGE_KEY);
    return isClassKind(stored) ? stored : DEFAULT_CLASS_KIND;
  } catch {
    return DEFAULT_CLASS_KIND;
  }
}

export function storeClassKind(kind: ClassKind): void {
  try {
    window.localStorage.setItem(CLASS_KIND_STORAGE_KEY, kind);
  } catch {
    // Private mode / blocked storage: the choice just won't persist.
  }
}

/** Resolves the effective filter for a student of the given type. */
export function resolveClassKind(
  stored: ClassKind,
  userType: string | undefined,
  isUserTypeLoading: boolean,
): { kind: ClassKind | null; showToggle: boolean } {
  if (isUserTypeLoading && userType === undefined) {
    // Unknown cohort: don't query yet, or a Clube E2E student would briefly
    // load an empty "regular" list before switching to "all".
    return { kind: null, showToggle: false };
  }
  if (userType === 'community') {
    return { kind: 'all', showToggle: false };
  }
  return { kind: stored, showToggle: true };
}

export function useClassKindFilter() {
  const { type, isLoading } = useUserType();
  const [stored, setStored] = useState<ClassKind>(readStoredClassKind);

  const setKind = useCallback((next: ClassKind) => {
    storeClassKind(next);
    setStored(next);
  }, []);

  const { kind, showToggle } = resolveClassKind(stored, type, isLoading);
  return { kind, setKind, showToggle };
}
